import cx from 'classnames';
import type { VoidFunctionComponent } from 'react';
import Section from './SettingsPageSection';
import Dropdown from '../../settings/shared/SettingsPageDropdown';
import { LabeledCheckbox } from '../../generic/Forms';
import { getCategoryDisplayName, IssueScopeKind } from '../../../../shared/issuesV2';
import type { SelectOption } from '../../../types';
import type { Category } from '../../../../shared/issuesV2';
import styles from './Settings.module.less';

//  TODO: pick these off the GenericIssueSettingsProps type
type Props = {
  category: Category;
  scopeType: IssueScopeKind.Global | IssueScopeKind.Project;
  isScanningEnabled: boolean;
  onToggleIsScanningEnabled: (isChecked: boolean) => void;
  policyOptions: Array<SelectOption<number | null>>;
  selectedPolicyOption: SelectOption<number | null>;
  onSelectedPolicyChange: (policyId: number | null) => void;
  isCICheckEnabled: boolean;
  onToggleIsCICheckEnabled: (isChecked: boolean) => void;
  filterOptions: Array<SelectOption<number | null>>;
  selectedFilterOption: SelectOption<number | null>;
  onSelectedFilterChange: (policyId: number | null) => void;
  canEditPolicy: boolean;
  canEditFilters: boolean;
};

/**
 * SettingsPageIssuesSection is a SettingsPageSection.Right component
 * for each of the issue category sections.
 */
const SettingsPageIssuesSection: VoidFunctionComponent<Props> = ({
  scopeType,
  category,
  isScanningEnabled,
  onToggleIsScanningEnabled,
  policyOptions,
  selectedPolicyOption,
  onSelectedPolicyChange,
  isCICheckEnabled,
  onToggleIsCICheckEnabled,
  filterOptions,
  selectedFilterOption,
  onSelectedFilterChange,
  canEditPolicy,
  canEditFilters,
}) => {
  const displayCategory = getCategoryDisplayName(category);
  const scopeProjectsText = scopeType === IssueScopeKind.Global ? 'all new projects' : 'this project';
  return (
    <>
      <Section.Heading>Scan for {displayCategory} issues</Section.Heading>
      <LabeledCheckbox
        label={`Enable ${displayCategory} scanning`}
        isChecked={isScanningEnabled}
        onChange={(_event, isChecked: boolean) => onToggleIsScanningEnabled(isChecked)}
        isDisabled={!canEditPolicy}
      />
      <Section.Paragraph>
        Enabling this will turn on {displayCategory} issues for {scopeProjectsText}.
      </Section.Paragraph>
      <Section.Indented isDisabled={!isScanningEnabled}>
        <div
          className={cx(styles.issueSection, {
            [styles.issueSectionDisabled]: !isScanningEnabled,
          })}
        >
          <Dropdown
            id={`settings-project-issues-${category}-policy`}
            label={`Default ${displayCategory} policy`}
            options={policyOptions}
            value={selectedPolicyOption}
            onChange={(option: SelectOption<string | number | null> | null) => {
              //  null is a valid value, it's how users can unset this.
              if (option?.value === null || option?.value) {
                onSelectedPolicyChange(option.value as number | null);
              }
            }}
            isDisabled={!isScanningEnabled || !canEditPolicy}
          />
          <Section.Paragraph>This policy will be applied by default to {scopeProjectsText}.</Section.Paragraph>
          <LabeledCheckbox
            label={`Fail CI/CD checks if ${displayCategory} issues matching this filter exist.`}
            isChecked={isCICheckEnabled}
            onChange={(_event, isChecked: boolean) => onToggleIsCICheckEnabled(isChecked)}
            isDisabled={!isScanningEnabled}
          />
        </div>
        <div
          className={cx(styles.issueSection, {
            [styles.issueSectionDisabled]: !isScanningEnabled || !isCICheckEnabled,
          })}
        >
          <Dropdown
            id={`settings-project-issues-${category}-filter`}
            label={`CI/CD ${displayCategory} filter`}
            options={filterOptions}
            value={selectedFilterOption}
            onChange={(option: SelectOption<string | number | null> | null) => {
              //  null is a valid value, it's how users can unset this.
              if (option?.value === null || option?.value) {
                onSelectedFilterChange(option.value as number | null);
              }
            }}
            isDisabled={!isScanningEnabled || !isCICheckEnabled || !canEditFilters}
          />
        </div>
      </Section.Indented>
    </>
  );
};

export default SettingsPageIssuesSection;
